import React, { useState, useEffect } from 'react';
import { Clock, Edit, FileText, Briefcase, BarChart3, Upload, Trash2, CheckCircle2, Search, Plus, User, File, ExternalLink, Calendar, MapPin, Building2, TrendingUp, Users, BrainCircuit, Bold, Italic, Underline, Type, AlignLeft, List, Link2, Check, X, Mail } from 'lucide-react';
import { supabase_get_vacancy_applications, queueBullhornSync } from '../services/supabaseService';
import { Vacancy } from '../types';

interface TabProps {
    vacancy: Vacancy;
}

// --- ACTIVITEIT TAB ---
export const VacancyActivityTab: React.FC<TabProps> = ({ vacancy }) => {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (vacancy?.id) {
            import('../services/supabaseService').then(({ supabase_get_vacancy_activity_timeline }) => {
                supabase_get_vacancy_activity_timeline(vacancy.id).then(data => {
                    setLogs(data);
                    setLoading(false);
                });
            });
        }
    }, [vacancy?.id]);

    return (
        <div className="w-full space-y-6 p-4">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden min-h-[300px]">
                <div className="absolute top-0 right-0 p-8 bg-slate-50 rounded-full blur-3xl -mr-10 -mt-10 opacity-50 pointer-events-none"></div>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                        <Clock size={20} className="text-slate-400" /> Tijdlijn
                    </h3>
                    {loading && <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest animate-pulse flex items-center gap-1">Laden...</div>}
                </div>
                
                {logs.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center p-10 text-center text-slate-400">
                         <Clock size={32} className="mb-3 opacity-20" />
                         <p className="text-sm font-medium">Nog geen opgeslagen activiteiten voor deze vacature.</p>
                         <p className="text-xs mt-1">Nieuwe sollicitaties worden hier in de toekomst direct gelogd.</p>
                    </div>
                )}

                {logs.length > 0 && (
                    <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
                        {logs.map((log) => (
                            <div key={log.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-slate-100 text-slate-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10 transition-colors">
                                    {log.type === 'create' ? <Plus size={16} className="text-blue-500" /> :
                                     log.type === 'edit' ? <Edit size={16} className="text-amber-500" /> :
                                     log.type === 'match' ? <CheckCircle2 size={16} className="text-emerald-500" /> :
                                     log.type === 'flow_step' ? <List size={16} className="text-violet-500" /> :
                                     <Briefcase size={16} className="text-indigo-500" />}
                                </div>
                                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl border border-slate-200 shadow-sm transition-colors hover:border-slate-300">
                                    <div className="flex items-center justify-between mb-1 gap-2">
                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 truncate">{log.user}</span>
                                        <time className="text-[10px] font-bold text-slate-400 shrink-0 text-right" title={log.absoluteDate}>{log.date}</time>
                                    </div>
                                    <p className="text-sm text-slate-700 font-medium">{log.action}</p>
                                    {log.type === 'flow_step' && Array.isArray(log.changeSummaries) && log.changeSummaries.length > 0 && (
                                        <ul className="mt-2 pl-3.5 space-y-0.5 border-l-2 border-violet-100 text-[11px] text-slate-600 font-medium list-disc marker:text-violet-400">
                                            {log.changeSummaries.map((line: string, i: number) => (
                                                <li key={i} className="pl-0.5">{line}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {log.type === 'flow_step' && (log.userRole || (log.userEmail && log.userEmail !== log.user)) && (
                                        <p className="text-[10px] text-slate-500 mt-1.5 font-medium">
                                            {log.userRole ? <span>Rol: {log.userRole}</span> : null}
                                            {log.userRole && log.userEmail && log.userEmail !== log.user ? <span className="mx-1.5 text-slate-300">·</span> : null}
                                            {log.userEmail && log.userEmail !== log.user ? <span className="truncate">{log.userEmail}</span> : null}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// --- NOTES TAB ---
export const VacancyNotesTab: React.FC<TabProps> = ({ vacancy }) => {
    const [notes, setNotes] = useState('');
    const [candidates, setCandidates] = useState<any[]>([]);
    const [selectedCandidate, setSelectedCandidate] = useState<string>('');
    const [action, setAction] = useState<string>('Prescreening');
    const [nextAction, setNextAction] = useState<'Geen' | 'Afspraak' | 'Taak'>('Geen');
    
    // Form fields
    const [referenceText, setReferenceText] = useState('');
    
    const [savedNotes, setSavedNotes] = useState<any[]>([
        { id: 1, text: 'Klant hecht veel waarde aan de bedrijfscultuur fit. Let hier extra op tijdens de screening.', date: '10-03-2026', action: 'Prescreening', candidate: 'Jasper van der Post' }
    ]);

    useEffect(() => {
        if (vacancy?.id) {
            supabase_get_vacancy_applications(vacancy.id).then(apps => {
                if (apps) setCandidates(apps.filter(a => !a.isDeleted));
            });
        }
    }, [vacancy?.id]);

    const handleSave = async () => {
        if (!notes.trim()) return;
        
        const candidateRecord = candidates.find(c => c.candidate_id === selectedCandidate);
        const candName = candidateRecord?.candidates?.name || 'Onbekend';
        const bullhornId = candidateRecord?.candidates?.bullhorn_id;
        
        // 1. Oogopslag opslaan in UI
        setSavedNotes([{ id: Date.now(), text: notes, date: new Date().toLocaleDateString('nl-NL'), action, candidate: selectedCandidate ? candName : '' }, ...savedNotes]);
        
        // 2. Queue into Bullhorn Outbound Sync (als kandidaat bestaat in Bullhorn)
        if (bullhornId && vacancy?.clientId) {
            queueBullhornSync(vacancy.clientId, 'Note', String(bullhornId), 'CREATE', {
                action: action,
                comments: notes
            });
        }
        
        setNotes('');
        alert(bullhornId ? 'Notitie opgeslagen in Wachtrij voor Bullhorn!' : 'Notitie lokaal opgeslagen (geen Bullhorn koppeling).');
    };

    const ACTIONS = ['Prescreening', 'Voorstel review', 'Interview voorbereiding', 'LinkedIn inmail', 'Klantbezoek'];

    return (
        <div className="w-full flex flex-col h-full bg-white relative">
            <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-[18px] font-medium text-slate-800 flex items-center gap-2">
                    <FileText size={18} className="text-slate-400"/> Notitie bewerken
                </h2>
            </div>
            
            <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
                {/* Left Column: Editor */}
                <div className="w-full lg:w-3/5 border-r border-slate-200 flex flex-col bg-white">
                    {/* Fake Toolbar Header */}
                    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 bg-slate-50/50">
                        <div className="flex items-center gap-4">
                            <button className="text-[12px] font-bold text-slate-500 hover:text-[#0073e6] uppercase tracking-wider flex items-center gap-1">
                                <FileText size={14}/> Notitiesjabloon <Check size={12}/>
                            </button>
                            <button className="text-[12px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-wider flex items-center gap-1">
                                Concepten (0)
                            </button>
                        </div>
                    </div>
                    
                    <div className="p-4 flex-1 flex flex-col">
                        <label className="text-[13px] text-slate-600 font-medium mb-2">Opmerkingen</label>
                        <div className="border border-slate-300 rounded-sm flex-1 flex flex-col bg-white overflow-hidden shadow-sm focus-within:ring-1 focus-within:border-[#0073e6] focus-within:ring-[#0073e6]">
                            {/* Toolbar */}
                            <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 bg-slate-50 text-slate-600">
                                <span className="text-[13px] px-2 py-1 hover:bg-slate-200 rounded cursor-pointer">Stijl</span>
                                <div className="w-px h-4 bg-slate-300 mx-1"></div>
                                <span className="text-[13px] px-2 py-1 hover:bg-slate-200 rounded cursor-pointer">Le...</span>
                                <div className="w-px h-4 bg-slate-300 mx-1"></div>
                                <button className="p-1 hover:bg-slate-200 rounded"><Bold size={14}/></button>
                                <button className="p-1 hover:bg-slate-200 rounded"><Italic size={14}/></button>
                                <button className="p-1 hover:bg-slate-200 rounded"><Underline size={14}/></button>
                                <button className="p-1 hover:bg-slate-200 rounded"><Type size={14}/></button>
                                <button className="p-1 hover:bg-slate-200 rounded"><AlignLeft size={14}/></button>
                                <button className="p-1 hover:bg-slate-200 rounded"><List size={14}/></button>
                                <button className="p-1 hover:bg-slate-200 rounded"><Link2 size={14}/></button>
                            </div>
                            {/* Editor Body */}
                            <textarea 
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Typ hier de notitie..."
                                className="w-full flex-1 p-4 resize-none border-none outline-none text-[14px] leading-relaxed text-slate-800"
                            ></textarea>
                            <div className="h-4 bg-slate-50 border-t border-slate-200 flex justify-end px-1 cursor-ns-resize">
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-slate-400 rotate-90 scale-75">
                                    <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Metadata */}
                <div className="w-full lg:w-2/5 flex flex-col bg-white overflow-y-auto custom-scrollbar p-6">
                    <div className="space-y-6">
                        
                        {/* Over Select */}
                        <div>
                            <label className="text-[13px] text-slate-600 font-medium mb-1 block">Over</label>
                            <div className="relative flex items-center border-b border-slate-300 focus-within:border-[#0073e6] transition-colors pb-1">
                                <Check size={14} className="text-emerald-500 mr-2 shrink-0"/>
                                <select 
                                    value={selectedCandidate} 
                                    onChange={e => setSelectedCandidate(e.target.value)}
                                    className="w-full appearance-none outline-none text-[14px] text-slate-800 bg-transparent block"
                                >
                                    <option value="">Kies kandidaat...</option>
                                    {candidates.map(c => (
                                        <option key={c.candidate_id} value={c.candidate_id}>{c.candidates?.name}</option>
                                    ))}
                                </select>
                                {selectedCandidate && <button onClick={() => setSelectedCandidate('')} className="p-1 text-slate-400 hover:text-slate-600 ml-auto"><X size={14}/></button>}
                            </div>
                        </div>

                        {/* Actie Select */}
                        <div>
                            <label className="text-[13px] text-slate-600 font-medium mb-1 block">Actie</label>
                            <div className="relative border-b border-slate-300 focus-within:border-[#0073e6] transition-colors pb-1">
                                <select 
                                    value={action} 
                                    onChange={e => setAction(e.target.value)}
                                    className="w-full appearance-none outline-none text-[14px] text-slate-800 bg-transparent block"
                                >
                                    {ACTIONS.map(act => (
                                        <option key={act} value={act}>{act}</option>
                                    ))}
                                </select>
                                <svg className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </div>
                        </div>

                        {/* Aanvullende referenties */}
                        <div>
                            <label className="text-[13px] text-slate-600 font-medium mb-2 block">Aanvullende referenties</label>
                            <div className="relative border-b border-red-400 pb-1 mb-1">
                                <div className="flex items-center gap-2 bg-white">
                                    <span className="flex items-center gap-2 bg-slate-100 text-[13px] text-slate-600 px-2 py-0.5 rounded-full inline-flex">
                                        <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
                                        {vacancy?.title}
                                        <X size={12} className="text-slate-400 ml-1 cursor-pointer hover:text-slate-600"/>
                                    </span>
                                </div>
                                <Search size={14} className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer"/>
                            </div>
                            <div className="flex justify-end">
                                <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest cursor-pointer hover:underline">Alles wissen X</span>
                            </div>
                        </div>

                        {/* Volgende actie plannen */}
                        <div>
                            <label className="text-[13px] text-slate-600 font-medium mb-2 block">Volgende actie plannen</label>
                            <div className="flex border border-[#0073e6] rounded-md overflow-hidden max-w-max">
                                <button 
                                    onClick={() => setNextAction('Geen')}
                                    className={`px-4 py-1.5 text-[13px] font-medium transition-colors ${nextAction === 'Geen' ? 'bg-[#0073e6] text-white' : 'bg-white text-[#0073e6] hover:bg-blue-50'}`}
                                >Geen</button>
                                <button 
                                    onClick={() => setNextAction('Afspraak')}
                                    className={`px-4 py-1.5 text-[13px] font-medium border-l border-[#0073e6] transition-colors ${nextAction === 'Afspraak' ? 'bg-[#0073e6] text-white' : 'bg-white text-[#0073e6] hover:bg-blue-50'}`}
                                >Afspraak</button>
                                <button 
                                    onClick={() => setNextAction('Taak')}
                                    className={`px-4 py-1.5 text-[13px] font-medium border-l border-[#0073e6] transition-colors ${nextAction === 'Taak' ? 'bg-[#0073e6] text-white' : 'bg-white text-[#0073e6] hover:bg-blue-50'}`}
                                >Taak</button>
                            </div>
                        </div>
                        
                        {/* E-mailmelding */}
                        <div className="pt-4">
                            <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 p-2 rounded mb-4">
                                <Mail size={16} className="text-slate-700"/>
                                <span className="text-[14px] font-bold text-slate-700">E-mailmelding</span>
                            </div>
                            
                            <div className="space-y-4">
                                <div className="relative border-b border-slate-300 pb-1 focus-within:border-[#0073e6] transition-colors">
                                    <input 
                                        type="text" 
                                        placeholder="Interne gebruiker" 
                                        className="w-full outline-none text-[13px] pt-1 placeholder:text-slate-500 text-slate-800"
                                    />
                                    <Search size={14} className="absolute right-0 top-1 text-slate-400 cursor-pointer"/>
                                </div>
                                <div className="relative border-b border-slate-300 pb-1 focus-within:border-[#0073e6] transition-colors">
                                    <input 
                                        type="text" 
                                        placeholder="Distributielijst" 
                                        className="w-full outline-none text-[13px] pt-1 placeholder:text-slate-500 text-slate-800"
                                    />
                                    <Search size={14} className="absolute right-0 top-1 text-slate-400 cursor-pointer"/>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Absolute positioning for the save button to mimic global save action */}
            <div className="absolute bottom-6 left-6 z-10 flex gap-2">
                 <button onClick={handleSave} className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-500 border border-emerald-200 shadow-sm hover:scale-110 transition-transform">
                     <Check size={16}/>
                 </button>
            </div>

            {/* Saved Notes List */}
            {savedNotes.length > 0 && (
                <div className="px-6 py-8 border-t border-slate-200 bg-[#f8fafc]">
                    <h4 className="text-[13px] font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <User size={16} className="text-slate-400"/> Recente Notities
                    </h4>
                    <div className="space-y-4">
                        {savedNotes.map(note => (
                            <div key={note.id} className="bg-white border text-left border-slate-200 p-5 rounded-lg flex flex-col gap-2 hover:shadow-sm transition-shadow">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900 text-sm">Jij</span>
                                        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-widest">{note.action}</span>
                                        {note.candidate && (
                                            <span className="text-[11px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Over: {note.candidate}</span>
                                        )}
                                    </div>
                                    <span className="text-[11px] text-slate-400 font-medium">{note.date}</span>
                                </div>
                                <p className="text-[14px] text-slate-700 leading-relaxed whitespace-pre-wrap">{note.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- FILES TAB ---
export const VacancyFilesTab: React.FC<TabProps> = ({ vacancy }) => {
    const defaultFiles = vacancy.data?.files || [];
    const [files, setFiles] = useState<any[]>(defaultFiles);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Sync if external vacancy data changes
    useEffect(() => {
        setFiles(vacancy.data?.files || []);
    }, [vacancy.data?.files]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>, isDrop = false) => {
        const selectedFiles = isDrop 
            ? (e as React.DragEvent<HTMLDivElement>).dataTransfer?.files 
            : (e as React.ChangeEvent<HTMLInputElement>).target?.files;

        if (!selectedFiles || selectedFiles.length === 0) return;
        
        setIsUploading(true);
        try {
            const uploadedFiles = [];
            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                // Allowed extensions roughly handled by accept attribute, but let's be safe
                const { supabase_upload_file, supabase } = await import('../services/supabaseService');
                const url = await supabase_upload_file(file, 'documents', 'vacancy-files/');
                
                if (url) {
                    const newFile = {
                        id: Date.now().toString() + i,
                        name: file.name,
                        url: url,
                        contentType: file.type,
                        dateAdded: new Date().toISOString(),
                        source: 'manual'
                    };
                    uploadedFiles.push(newFile);
                    
                    // Save to DB (documents table)
                    const clientId = (vacancy.data as any)?.client_id || vacancy.clientId;
                    if (clientId) {
                         await supabase.from('documents').insert([{
                             title: file.name,
                             storage_path: url,
                             file_type: file.name.split('.').pop() || 'unknown',
                             vacancy_id: (vacancy.id && vacancy.id.length === 36) ? vacancy.id : null,
                             client_id: clientId
                         }]);
                    }
                }
            }

            if (uploadedFiles.length > 0) {
                const updatedFiles = [...files, ...uploadedFiles];
                setFiles(updatedFiles);
                
                // Update vacancy data
                const { supabase } = await import('../services/supabaseService');
                const newData = { ...vacancy.data, files: updatedFiles };
                await supabase.from('vacancies').update({ data: newData }).eq('id', vacancy.id);
            }
        } catch (error) {
            console.error("Upload error", error);
            alert("Er is een fout opgetreden bij het uploaden.");
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = ''; // Reset
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        await handleFileChange(e as any, true);
    };

    return (
        <div className="w-full space-y-6 p-6">
            <div className="flex justify-between items-center bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <div>
                    <h3 className="text-lg font-black text-slate-900">Bestanden</h3>
                    <p className="text-xs font-medium text-slate-500 mt-1">Sla contracten, extra briefings of bijlages centraal op.</p>
                </div>
                <button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-colors shadow-sm disabled:opacity-50"
                >
                    <Upload size={14} /> {isUploading ? 'Uploaden...' : 'Uploaden'}
                </button>
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    multiple 
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, false)} 
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {files.map((file: any) => (
                    <div key={file.id} className="bg-white p-5 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all group flex flex-col">
                        <div className="w-12 h-12 bg-indigo-50 text-indigo-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            {file.contentType?.includes('pdf') ? <FileText size={24} /> : <File size={24} />}
                        </div>
                        <h4 className="font-bold text-slate-900 mb-1 truncate" title={file.name}>{file.name}</h4>
                        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mb-4">
                            Toegevoegd op {file.dateAdded ? new Date(file.dateAdded).toLocaleDateString() : 'Onbekend'}
                        </p>
                        <div className="flex justify-between mt-auto pt-4 border-t border-slate-100">
                            {file.source === 'bullhorn' ? (
                                <span className="text-xs font-bold text-blue-500 bg-blue-50 px-2 py-1 rounded-md">Bullhorn</span>
                            ) : (
                                <button className="text-slate-400 hover:text-indigo-600 text-xs font-bold uppercase transition-colors">Bekijken</button>
                            )}
                            <button className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={14}/></button>
                        </div>
                    </div>
                ))}

                <div 
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-2xl flex flex-col items-center justify-center p-8 text-center transition-colors cursor-pointer min-h-[200px] ${
                        isDragging ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                >
                    <Upload size={24} className={`${isDragging ? 'text-indigo-400' : 'text-slate-300'} mb-3`} />
                    <p className={`text-sm font-bold ${isDragging ? 'text-indigo-600' : 'text-slate-600'}`}>Klik of sleep bestand</p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest">PDF, DOCX, JPG/PNG</p>
                </div>
            </div>
        </div>
    );
};

// --- JOB BOARDS TAB ---
export const VacancyJobBoardsTab: React.FC<TabProps> = ({ vacancy }) => {
    return (
        <div className="w-full space-y-6 p-6">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white shadow-lg flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-black mb-2 flex items-center gap-2">
                        Multiposten <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest ml-2">Premium</span>
                    </h2>
                    <p className="text-blue-100 text-sm max-w-md">Koppel met honderden job boards en beheer je externe plaatsingen direct vanuit het dashboard.</p>
                </div>
                <div className="hidden md:flex gap-2">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-blue-600 font-black text-xl shadow-inner">in</div>
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-blue-600 font-bold text-xs shadow-inner uppercase">Indeed</div>
                </div>
            </div>

            <div className="bg-white border text-center border-slate-200 p-12 rounded-2xl flex flex-col items-center justify-center shadow-sm">
                <Briefcase size={32} className="text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">Multiposting is nog niet geconfigureerd</h3>
                <p className="text-sm text-slate-500 max-w-md mb-6">Neem contact op met support om de API-koppeling voor automatische job board plaatsingen voor jouw account te activeren.</p>
                <button className="bg-white border border-slate-200 text-slate-900 px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-50 transition-colors shadow-sm">
                    Activeer Multiposting <ExternalLink size={14} />
                </button>
            </div>
        </div>
    );
};

// --- ANALYSE TAB ---
export const VacancyAnalyticsTab: React.FC<TabProps> = ({ vacancy }) => {
    const [apps, setApps] = useState<any[]>([]);
    
    useEffect(() => {
        if(vacancy?.id) {
            supabase_get_vacancy_applications(vacancy.id).then(data => {
                if(data) setApps(data);
            });
        }
    }, [vacancy?.id]);

    const activeApps = apps.filter(a => !a.isDeleted);
    const aiMatches = activeApps.filter(a => (a.match_score || 0) >= 70);
    
    // Calculate days online (fallback to 1 if just created)
    const createdDate = new Date((vacancy as any).created_at || vacancy.createdAt || Date.now());
    const diffTime = Math.abs(Date.now() - createdDate.getTime());
    const daysOnline = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    
    return (
        <div className="w-full space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 bg-emerald-50 rounded-bl-3xl">
                        <Users size={20} className="text-emerald-500" />
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Totaal in Pipeline</p>
                    <h3 className="text-3xl font-black text-slate-900">{activeApps.length}</h3>
                    <p className="text-xs font-bold text-emerald-500 mt-2 flex items-center gap-1">Actieve kandidaten</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 bg-indigo-50 rounded-bl-3xl">
                        <FileText size={20} className="text-indigo-500" />
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">In Proces</p>
                    <h3 className="text-3xl font-black text-slate-900">{activeApps.filter(a => a.status !== 'New' && a.status !== 'Gediskwalificeerd').length}</h3>
                    <p className="text-xs font-bold text-indigo-500 mt-2">Gesproken of verder</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 bg-amber-50 rounded-bl-3xl">
                        <BrainCircuit size={20} className="text-amber-500" />
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Goede AI Matches</p>
                    <h3 className="text-3xl font-black text-slate-900">{aiMatches.length}</h3>
                    <p className="text-xs font-bold text-amber-500 mt-2">Score &gt; 70%</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 bg-red-50 rounded-bl-3xl">
                        <Calendar size={20} className="text-red-500" />
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Dagen Online</p>
                    <h3 className="text-3xl font-black text-slate-900">{daysOnline}</h3>
                    <p className="text-xs font-bold text-slate-400 mt-2">Sinds creatie</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm h-80 flex flex-col items-center justify-center text-center">
                    <BarChart3 size={48} className="text-slate-200 mb-4" />
                    <h4 className="font-bold text-slate-900 mb-1">Instroom Funnel</h4>
                    <p className="text-xs text-slate-500">Gedetailleerde funnel analyse wordt binnenkort geactiveerd voor deze weergave.</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm h-80 flex flex-col items-center justify-center text-center">
                    <MapPin size={48} className="text-slate-200 mb-4" />
                    <h4 className="font-bold text-slate-900 mb-1">Kandidaat Herkomst</h4>
                    <p className="text-xs text-slate-500">Geografische en bron-analyse wordt binnenkort geactiveerd.</p>
                </div>
            </div>
        </div>
    );
};
